import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

/** Days from `todayDay` to the [fromIso, toIso] window; 0 if today falls inside it. */
private fun distanceToRange(todayDay: Double, fromIso: String, toIso: String): Double {
    val from = isoEpochDay(fromIso)
    val to = isoEpochDay(toIso)
    return when {
        todayDay < from -> from - todayDay
        todayDay > to -> todayDay - to
        else -> 0.0
    }
}

/**
 * The round currently "on turn" by date (today falls in its Termínová listina window), or the
 * closest one by date if none matches (e.g. an off-season gap) -- falls back to the first option
 * if no round-slot dates are available at all.
 */
private fun pickCurrentRound(
    options: List<Pair<String, Int>>,
    slotByRound: Map<Int, SeasonRoundSlotDto>,
): Pair<String, Int>? {
    if (options.isEmpty()) return null
    val today = isoEpochDay(todayIso())
    return options.minByOrNull { (_, round) ->
        val slot = slotByRound[round] ?: return@minByOrNull Double.MAX_VALUE
        distanceToRange(today, slot.dateFrom, slot.dateTo ?: slot.dateFrom)
    }
}

@Composable
fun MatchEntryScreen(
    token: String?,
    role: String?,
    activeSeason: SeasonDto?,
    activeLeagues: List<SeasonLeagueDto>,
    // Výběr ligy/kola je držený v App() (přežije odskok na výsledek zápasu a zpět) -- viz `navigate`.
    selectedLeagueId: String,
    onSelectLeague: (String) -> Unit,
    selectedRoundKey: String,
    onSelectRound: (String) -> Unit,
    onMatchClick: (match: MatchDto, homeName: String, awayName: String, leagueName: String) -> Unit,
) {
    var matches by remember { mutableStateOf<List<MatchDto>>(emptyList()) }
    var teamNames by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var roundSlots by remember { mutableStateOf<List<SeasonRoundSlotDto>>(emptyList()) }
    var roundSlotsLoaded by remember { mutableStateOf(false) }
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(activeLeagues) {
        if (selectedLeagueId.isBlank() && activeLeagues.isNotEmpty()) {
            onSelectLeague(activeLeagues.first().id)
        }
    }

    // Round-date windows are season-wide (Termínová listina), independent of the selected league.
    LaunchedEffect(activeSeason?.id) {
        val seasonId = activeSeason?.id
        roundSlots = if (seasonId != null) {
            try { Api.fetchRoundSlots(token, seasonId) } catch (e: Throwable) { emptyList() }
        } else {
            emptyList()
        }
        roundSlotsLoaded = true
    }

    LaunchedEffect(selectedLeagueId) {
        if (selectedLeagueId.isBlank()) return@LaunchedEffect
        loading = true
        // Pozn.: kolo se NEnuluje tady (běží to i při remountu = návratu z výsledku, kdy chceme výběr
        // zachovat). Vynuluje se jen při aktivní změně ligy v dropdownu (viz onChange u Select níž).
        try {
            matches = Api.fetchMatches(token, selectedLeagueId)
            activeSeason?.let { season ->
                teamNames = Api.fetchAllTeamsForSeason(token, season.id).associate { it.teamId to (it.teamNameOverride ?: it.team?.name ?: it.teamId) }
            }
            error = null
        } catch (e: Throwable) {
            error = "Načtení zápasů selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    // Nadstavba (spec 12): O titul and O udržení share the same round -- one dropdown entry, not
    // two, listing matches from both groups together (unlike entering a result for one specific
    // match, picking a round here is just about finding it in the list below).
    val roundOptions = remember(matches) {
        roundOptionsOf(matches.map { it.copy(phase = canonicalPlayoffPhase(it.phase)) })
    }
    val slotByRound = remember(roundSlots) { roundSlots.associateBy { it.roundNumber } }

    // Defaults to whichever round is currently on turn by date (or nearest); waits for the
    // round-slot fetch to settle first so it doesn't lock in the plain first-round fallback early.
    // Pozn.: hlídá se PLATNOST vybraného kola, ne jen prázdná hodnota -- `roundOptions` je obyčejný
    // seznam a LaunchedEffect porovnává klíče strukturálně, takže po změně ligy na ligu se stejnými
    // koly by se efekt nerestartoval a vynulované kolo by už nikdo nedoplnil (= "Pro tento výběr
    // nejsou žádné zápasy"). Stejně tak přepnutí na ligu s menším počtem kol může nechat vybrané
    // kolo, které v nové lize neexistuje.
    LaunchedEffect(roundOptions, roundSlotsLoaded, selectedRoundKey) {
        if (roundOptions.isEmpty() || !roundSlotsLoaded) return@LaunchedEffect
        val selectionIsOffered = roundOptions.any { (phase, round) -> roundKey(phase, round) == selectedRoundKey }
        if (!selectionIsOffered) {
            val (phase, round) = pickCurrentRound(roundOptions, slotByRound) ?: roundOptions.first()
            onSelectRound(roundKey(phase, round))
        }
    }

    val filteredMatches = remember(matches, selectedRoundKey) {
        matches.filter { roundKey(canonicalPlayoffPhase(it.phase), it.roundNumber) == selectedRoundKey }
    }
    val selectedLeagueName = activeLeagues.find { it.id == selectedLeagueId }?.name ?: ""

    Div(attrs = { classes("card") }) {
        H2 { Text("Zápis o utkání") }

        if (!role.canScore()) {
            Text("Pro zadání výsledku se musíš přihlásit jako scorer/admin/superadmin.")
            return@Div
        }
        if (activeSeason == null) {
            Text("Právě není žádná aktivní sezóna.")
            return@Div
        }
        if (activeLeagues.isEmpty()) {
            Text("Aktivní sezóna zatím nemá žádnou ligu.")
            return@Div
        }

        Div(attrs = { classes("actions") }) {
            Select(attrs = { onChange {
                // Aktivní změna ligy -> vynuluj kolo, ať auto-výběr naskočí na aktuální kolo nové ligy.
                onSelectLeague(it.value ?: "")
                onSelectRound("")
            } }) {
                activeLeagues.forEach { league ->
                    Option(league.id, attrs = { if (league.id == selectedLeagueId) selected() }) { Text(league.name) }
                }
            }
            Select(attrs = { onChange { onSelectRound(it.value ?: "") } }) {
                roundOptions.forEach { (phase, round) ->
                    val key = roundKey(phase, round)
                    Option(key, attrs = { if (key == selectedRoundKey) selected() }) { Text(roundSlotLabel(phase, round)) }
                }
            }
        }

        if (loading) Text("Načítám…")
        error?.let { Div { Text("Chyba: $it") } }

        if (!loading && filteredMatches.isEmpty()) {
            Div { Text("Pro tento výběr nejsou žádné zápasy.") }
        } else if (!loading) {
            Table(attrs = { classes("schedule-table") }) {
                Tr {
                    Th { Text("Domácí") }
                    Th { Text("Hosté") }
                    Th { Text("Výsledek") }
                }
                filteredMatches.forEach { m ->
                    val homeName = teamNames[m.homeTeamId] ?: m.homeTeamId
                    val awayName = teamNames[m.awayTeamId] ?: m.awayTeamId
                    Tr(attrs = { onClick { onMatchClick(m, homeName, awayName, selectedLeagueName) } }) {
                        Td { Text(homeName) }
                        Td { Text(awayName) }
                        Td { Text(if (m.status == "completed") "${m.homeDuelPoints ?: 0}:${m.awayDuelPoints ?: 0}" else "neodehráno") }
                    }
                }
            }
        }
    }
}
